import { concat, from, of } from 'rxjs';
import workflow from '@banking/ui/lib/workflow';
import { BalanceReportItemState, BalanceReportItemTypeCategory } from '@square/dashboard-protos/models/squareup/balancereporter/model';
import { Owner, OwnerType } from '@square/dashboard-protos/models/squareup/banking/common';
import { Money, Currency } from '@square/dashboard-protos/models/squareup/connect/v2/common/money';
import { groupCompletedActivitiesByDay } from '@banking/ui/utils/activity';
import { momentFromDateTimeWithZone } from '@banking/ui/utils/datetime-converter';
import moment from 'moment';
import { BootstrapPrintableCheckManagementRequest as BootstrapPrintableCheckManagementRequestProto, CancelPrintableCheckRequest as CancelPrintableCheckRequestProto } from '@square/dashboard-protos/models/squareup/bbfrontend/common/printable_check/printable_check_requests';
import { ExternalOverrideDeclinedAuthRequest, ExternalRetrieveAuthStateRequest } from '@square/dashboard-protos/models/squareup/transaction/transaction_service';
import { ListCategoriesRequest, UpdateCategoryRequest } from '@square/dashboard-protos/models/squareup/bbfrontend/common/categories/categories_requests';
import { TransactionCategory } from '@square/dashboard-protos/models/squareup/bbfrontend/common/categories/categories';
import assert from '@banking/ui/utils/assert';
import { v4 as uuid } from 'uuid';
import { clickCategoryRow, clickUpdateCategory } from '@banking/ui/utils/categories-cdp-events';
export const DEFAULT_BATCH_SIZE = 20;
export const BASEURL = {
  balancereporter: '/v1/balance-reporter/reports/all-activity',
  bizbank: '/v1/bb/reports/all-activity',
  sfscore: '/sfs/api/v1/export_transactions'
};
const IGNORED_TRANSACTION_STATES = new Set([BalanceReportItemState.INITIATED, BalanceReportItemState.VOIDED, BalanceReportItemState.DECLINED]);
export const ALL_TYPE_CATEGORY = {
  label: 'banking.checking.activity.typeFilter.all',
  value: [BalanceReportItemTypeCategory.PAYMENT_PROCESSING, BalanceReportItemTypeCategory.CARD_SPEND, BalanceReportItemTypeCategory.TRANSFERS]
};
export const CARD_SPEND_CATEGORY = {
  label: 'banking.checking.activity.typeFilter.spend',
  value: [BalanceReportItemTypeCategory.CARD_SPEND]
};
export const TRANSFERS_TYPE_CATEGORY = {
  label: 'banking.checking.activity.typeFilter.transfers',
  value: [BalanceReportItemTypeCategory.TRANSFERS]
};
export const TYPE_CATEGORIES = [ALL_TYPE_CATEGORY, CARD_SPEND_CATEGORY, TRANSFERS_TYPE_CATEGORY, {
  label: 'banking.checking.activity.typeFilter.sales',
  value: [BalanceReportItemTypeCategory.PAYMENT_PROCESSING]
}];
export const INITIAL_QUERY = {
  activityOwners: [],
  selectedTypeCategory: ALL_TYPE_CATEGORY,
  selectedDateCategory: 'this-year',
  startAt: undefined,
  endAt: undefined
};
export let MarketToastTypes = /*#__PURE__*/function (MarketToastTypes) {
  MarketToastTypes[MarketToastTypes["CRITICAL"] = 0] = "CRITICAL";
  MarketToastTypes[MarketToastTypes["INFO"] = 1] = "INFO";
  MarketToastTypes[MarketToastTypes["SUCCESS"] = 2] = "SUCCESS";
  MarketToastTypes[MarketToastTypes["WARNING"] = 3] = "WARNING";
  return MarketToastTypes;
}({});
export const INITIAL_STATE = {
  activityList: {
    pending: [],
    completed: []
  },
  config: {
    requireExportLink: true,
    enablePostedBalance: true,
    typeCategories: TYPE_CATEGORIES,
    activityPageSize: DEFAULT_BATCH_SIZE
  },
  completedActivities: [],
  completedPaginationToken: null,
  expandedBundles: new Set(),
  exportLink: '',
  formattedDetail: {
    title: '',
    amount: new Money.Builder().amount(0).currency(Currency.USD).build(),
    stateCategory: '',
    lineItems: [],
    id: null,
    transactionToken: null,
    isPrintableCheck: false
  },
  hasError: false,
  hasBladeError: false,
  isBladeOpen: false,
  isCancelPrintableCheckLoading: false,
  loading: false,
  loadingDetails: false,
  loadingNextPage: false,
  pendingActivities: [],
  pendingPaginationToken: null,
  unitToken: '',
  query: INITIAL_QUERY,
  showCancelPrintableCheckDialog: false,
  showCategories: false
};
// ---------------------------------------
export let ActionType = /*#__PURE__*/function (ActionType) {
  ActionType["ActivityDetailsRequest"] = "ACTIVITY_DETAILS_REQUEST";
  ActionType["ActivityDetailsResponse"] = "ACTIVITY_DETAILS_RESPONSE";
  ActionType["BootstrapPrintableCheckManagementRequest"] = "BOOTSTRAP_PRINTABLE_CHECK_MANAGEMENT_REQUEST";
  ActionType["BootstrapPrintableCheckManagementResponse"] = "BOOTSTRAP_PRINTABLE_CHECK_MANAGEMENT_RESPONSE";
  ActionType["CancelPrintableCheckRequest"] = "CANCEL_PRINTABLE_CHECK_REQUEST";
  ActionType["CancelPrintableCheckResponse"] = "CANCEL_PRINTABLE_CHECK_RESPONSE";
  ActionType["ToggleCancelPrintableCheck"] = "TOGGLE_CANCEL_PRINTABLE_CHECK";
  ActionType["CloseBundle"] = "CLOSE_BUNDLE";
  ActionType["CompletedActivitiesRequest"] = "COMPLETED_ACTIVITIES_REQUEST";
  ActionType["CompletedActivitiesResponse"] = "COMPLETED_ACTIVITIES_RESPONSE";
  ActionType["DownloadCheck"] = "DOWNLOAD_CHECK";
  ActionType["PendingActivitiesRequest"] = "PENDING_ACTIVITIES_REQUEST";
  ActionType["PendingActivitiesResponse"] = "PENDING_ACTIVITIES_RESPONSE";
  ActionType["PrintCheck"] = "PRINT_CHECK";
  ActionType["Scroll"] = "SCROLL";
  ActionType["SetDetailsLoading"] = "SET_DETAILS_LOADING";
  ActionType["SetLoading"] = "SET_LOADING";
  ActionType["SetPaginationLoading"] = "SET_PAGINATION_LOADING";
  ActionType["OpenBundle"] = "OPEN_BUNDLE";
  ActionType["RecognizeTransactionRequest"] = "RECOGNIZE_TRANSACTION_REQUEST";
  ActionType["RecognizeTransactionResponse"] = "RECOGNIZE_TRANSACTION_RESPONSE";
  ActionType["RetrieveTransactionAuthRequest"] = "RETRIEVE_TRANSACTION_AUTH_REQUEST";
  ActionType["RetrieveTransactionAuthResponse"] = "RETRIEVE_TRANSACTION_AUTH_RESPONSE";
  ActionType["Reset"] = "RESET";
  ActionType["UpdateDateRange"] = "UPDATE_DATE_RANGE";
  ActionType["UpdateDateMenuSelection"] = "UPDATE_DATE_MENU_SELECTION";
  ActionType["UpdateOwners"] = "UPDATE_OWNERS";
  ActionType["UpdateOwnersAndSetDateRange"] = "UPDATE_OWNERS_AND_SET_DATE_RANGE";
  ActionType["UpdateQuery"] = "UPDATE_QUERY";
  ActionType["UpdateSelectedType"] = "UPDATE_TYPES";
  ActionType["ViewInTransaction"] = "VIEW_IN_TRANSACTION";
  ActionType["GoToActivityRoute"] = "GO_TO_ACTIVITY_ROUTE";
  ActionType["ListCategories"] = "LIST_CATEGORIES";
  ActionType["ListCategoriesResponse"] = "LIST_CATEGORIES_RESPONSE";
  ActionType["ToggleCategories"] = "TOGGLE_CATEGORIES";
  ActionType["UpdateCategory"] = "UPDATE_CATEGORY";
  ActionType["UpdateCategoryResponse"] = "UPDATE_CATEGORY_RESPONSE";
  ActionType["OpenBlade"] = "OPEN_BLADE";
  ActionType["RemoveBlade"] = "REMOVE_BLADE";
  ActionType["UpdateConfig"] = "UPDATE_CONFIG";
  ActionType["ShowToast"] = "SHOW_TOAST";
  ActionType["ShowCategoryError"] = "SHOW_CATEGORY_ERROR";
  ActionType["ListCategoriesError"] = "LIST_CATEGORIES_ERROR";
  return ActionType;
}({});
export const activityDetailsRequestAction = id => ({
  type: ActionType.ActivityDetailsRequest,
  id
});
export const activityDetailsResponseAction = detail => ({
  type: ActionType.ActivityDetailsResponse,
  detail
});
export const bootstrapPrintableCheckManagementRequestAction = () => ({
  type: ActionType.BootstrapPrintableCheckManagementRequest
});
export const bootstrapPrintableCheckManagementResponseAction = response => ({
  type: ActionType.BootstrapPrintableCheckManagementResponse,
  response
});
export const cancelPrintableCheckRequestAction = () => ({
  type: ActionType.CancelPrintableCheckRequest
});
export const cancelPrintableCheckResponseAction = response => ({
  type: ActionType.CancelPrintableCheckResponse,
  response
});
export const toggleCancelPrintableCheckAction = () => ({
  type: ActionType.ToggleCancelPrintableCheck
});
export const setDetailsLoadingAction = loadingDetails => ({
  type: ActionType.SetDetailsLoading,
  loadingDetails
});
export const setLoadingAction = loading => ({
  type: ActionType.SetLoading,
  loading
});
export const setPaginationLoadingAction = loadingNextPage => ({
  type: ActionType.SetPaginationLoading,
  loadingNextPage
});
export const pendingActivitiesRequestAction = () => ({
  type: ActionType.PendingActivitiesRequest
});
export const pendingActivitiesResponseAction = (query, data) => ({
  type: ActionType.PendingActivitiesResponse,
  query,
  data
});
export const closeBundleAction = bundleId => ({
  type: ActionType.CloseBundle,
  bundleId
});
export const completedActivitiesRequestAction = () => ({
  type: ActionType.CompletedActivitiesRequest
});
export const completedActivitiesResponseAction = (query, data) => ({
  type: ActionType.CompletedActivitiesResponse,
  query,
  data
});
export const openBundleAction = bundleId => ({
  type: ActionType.OpenBundle,
  bundleId
});
export const resetAction = () => ({
  type: ActionType.Reset
});
export const updateDateMenuSelectionAction = selectedDateCategory => ({
  type: ActionType.UpdateDateMenuSelection,
  selectedDateCategory
});
export const updateDateRangeAction = (startAt, endAt) => ({
  type: ActionType.UpdateDateRange,
  startAt,
  endAt
});
export const updateOwnersAction = unitToken => ({
  type: ActionType.UpdateOwners,
  unitToken
});
export const updateOwnersAndSetDateRangeAction = (unitToken, startAt, endAt) => ({
  type: ActionType.UpdateOwnersAndSetDateRange,
  unitToken,
  startAt,
  endAt
});
export const updateQueryAction = query => ({
  type: ActionType.UpdateQuery,
  query
});
export const updateSelectedTypeAction = selectedTypeCategory => ({
  type: ActionType.UpdateSelectedType,
  selectedTypeCategory
});
export const scrollAction = () => ({
  type: ActionType.Scroll
});
export const viewInTransactionAction = params => ({
  type: ActionType.ViewInTransaction,
  route: 'transaction',
  params
});
export const goToActivityRouteAction = (route = 'activity') => ({
  type: ActionType.GoToActivityRoute,
  route
});
export const updateCategoryAction = update => ({
  type: ActionType.UpdateCategory,
  update
});
export const updateCategoryResponseAction = (id, response) => ({
  type: ActionType.UpdateCategoryResponse,
  id,
  response
});
export const showCategoryErrorAction = () => ({
  type: ActionType.ShowCategoryError
});
export const listCategoriesErrorAction = () => ({
  type: ActionType.ListCategoriesError
});
export const listCategoriesAction = () => ({
  type: ActionType.ListCategories
});
export const listCategoriesResponseAction = response => ({
  type: ActionType.ListCategoriesResponse,
  response
});
export const toggleCategoriesAction = () => ({
  type: ActionType.ToggleCategories
});
export const openBladeAction = activityId => ({
  type: ActionType.OpenBlade,
  isBladeOpen: true,
  id: activityId
});
export const removeBladeAction = () => ({
  type: ActionType.RemoveBlade,
  isBladeOpen: false
});
export const downloadCheckAction = printableCheckId => ({
  type: ActionType.DownloadCheck,
  printableCheckId
});
export const printCheckAction = printableCheckId => ({
  type: ActionType.PrintCheck,
  printableCheckId
});
export const retrieveTransactionAuthRequestAction = () => ({
  type: ActionType.RetrieveTransactionAuthRequest
});
export const retrieveTransactionAuthResponseAction = response => ({
  type: ActionType.RetrieveTransactionAuthResponse,
  response
});
export const recognizeTransactionRequestAction = isRecognized => ({
  type: ActionType.RecognizeTransactionRequest,
  isRecognized
});
export const recognizeTransactionResponseAction = response => ({
  type: ActionType.RecognizeTransactionResponse,
  response
});
export const updateConfigAction = foldersActivity => ({
  type: ActionType.UpdateConfig,
  foldersActivity
});
export const showToastAction = (toastType, toastText) => ({
  type: ActionType.ShowToast,
  toastType,
  toastText
});

// ---------------------------------------

export const allActivities = state => [...state.pendingActivities, ...state.completedActivities];
export const recentActivities = state => allActivities(state).slice(0, 5);
export const showPreviousBalance = state => state.config.enablePostedBalance && state.completedActivities.length > 0 && (state.query.selectedTypeCategory === ALL_TYPE_CATEGORY || state.query.selectedTypeCategory === CARD_SPEND_CATEGORY);
export const showTransfersFooter = state => state.query.selectedTypeCategory === TRANSFERS_TYPE_CATEGORY;
export const hasActivities = state => allActivities(state).length > 0;
export const hasNeverHadActivities = state => !hasActivities(state) && !state.query.startAt && !state.query.endAt && state.query.selectedTypeCategory === ALL_TYPE_CATEGORY;
export const startAtFormatted = state => state.query.startAt ? momentFromDateTimeWithZone(state.query.startAt).toDate().toISOString() : null;
export const endAtFormatted = state => state.query.endAt ? momentFromDateTimeWithZone(state.query.endAt).toDate().toISOString() : null;
export const selectedDateCategoryFormatted = state => {
  const uncapitalized = state.query.selectedDateCategory.replace('-', ' ');
  return uncapitalized.charAt(0).toUpperCase() + uncapitalized.slice(1);
};
export const isExportDisabled = state => state.completedActivities.length === 0 && state.pendingActivities.length === 0;
export const collapsedCsvExportLink = state => {
  return getExportLink(state, true);
};
export const expandedCsvExportLink = state => {
  return getExportLink(state, false);
};
export const startingBalance = state => {
  if (state.completedActivities.length <= 0) {
    return Money.create({
      currency: Currency.USD,
      amount: 0
    });
  }
  const lastActivity = state.completedActivities[state.completedActivities.length - 1];
  if (!lastActivity || !lastActivity.balance || !lastActivity.amount) {
    return Money.create({
      currency: Currency.USD,
      amount: 0
    });
  }
  if (lastActivity.state != null && IGNORED_TRANSACTION_STATES.has(lastActivity.state)) {
    return Money.create({
      amount: lastActivity.balance.amount,
      currency: lastActivity.balance.currency
    });
  } else {
    return Money.create({
      amount: lastActivity.balance.amount - lastActivity.amount.amount,
      currency: lastActivity.balance.currency
    });
  }
};

// Helper functions
// ---------------------------------------

function getExportLink(state, isCsvCollapsed = true) {
  if (state.query.activityOwners.length <= 0 || !state.query.activityOwners[0]) {
    throw new Error('No unitToken specified for getExportLink');
  }
  const owner = state.query.activityOwners[0];
  let baseUrl;
  let startTime;
  let endTime;

  // set baseUrl depend on if sfs account or balance reporter
  if (owner.type === 'account') {
    baseUrl = `${BASEURL.sfscore}?account_id=${owner.accountId}`;
  } else {
    baseUrl = `${BASEURL.balancereporter}?unitToken=${owner.id}`;
  }

  // If no date range is selected, default to YTD
  if (!state.query.startAt && !state.query.endAt) {
    startTime = encodeURIComponent(moment().startOf('year').format());
    endTime = encodeURIComponent(moment().format());
  }
  if (state.query.startAt) {
    startTime = encodeURIComponent(momentFromDateTimeWithZone(state.query.startAt).format());
  }
  if (state.query.endAt) {
    endTime = encodeURIComponent(momentFromDateTimeWithZone(state.query.endAt).format());
  }

  // If single day selected (valid startAt, no endAt), then set the endAt to the end of the day
  if (state.query.startAt && !state.query.endAt) {
    endTime = encodeURIComponent(momentFromDateTimeWithZone(state.query.startAt).endOf('day').format());
  }
  if (startTime && endTime) {
    if (owner.type === 'account') {
      baseUrl += `&is_collapsed=${isCsvCollapsed}&start_date=${startTime}&end_date=${endTime}`;
    } else {
      baseUrl += `&beginTime=${startTime}&endTime=${endTime}`;
    }
  }
  return baseUrl;
}

// Converts DateTime to a JavaScript Date object
function convertDateTimeToDate(dateTime) {
  return dateTime ? new Date(dateTime.instantUsec / 1000) : new Date(); // Return as a JavaScript Date object
}

// some request with startAt and endAt can take long time to resolve
// and the api result can be stale if startAt and endAt are changed
// this function is used to check if the request is stale
export function isStaleRequest(requestQuery, currentQuery) {
  const currentStartAt = convertDateTimeToDate(currentQuery.startAt);
  const currentEndAt = convertDateTimeToDate(currentQuery.endAt);
  const requestStartAt = convertDateTimeToDate(requestQuery.startAt);
  const requestEndAt = convertDateTimeToDate(requestQuery.endAt);
  return currentStartAt.getTime() !== requestStartAt.getTime() || currentEndAt.getTime() !== requestEndAt.getTime();
}
export const update = context =>
// eslint-disable-next-line complexity
(state, action) => {
  switch (action.type) {
    case ActionType.ActivityDetailsRequest:
      {
        const {
          id
        } = action;
        const work = () => {
          const action = context.getActivityDetails(id, state.query).then(activityDetailsResponseAction);
          return concat(of(setDetailsLoadingAction(true)), from(action));
        };
        return [state, work];
      }
    case ActionType.ActivityDetailsResponse:
      {
        const {
          detail
        } = action;
        if (detail instanceof Error) {
          context.sentry.captureException(detail);
          return [{
            ...state,
            hasBladeError: true
          }, () => of(setDetailsLoadingAction(false))];
        }
        const newState = {
          ...state,
          hasBladeError: false,
          formattedDetail: detail
        };
        if (detail.isPrintableCheck) {
          return [newState, () => of(bootstrapPrintableCheckManagementRequestAction())];
        }
        if (detail.authenticationNeeded) {
          return [newState, () => of(retrieveTransactionAuthRequestAction())];
        }
        return [newState, () => of(setDetailsLoadingAction(false))];
      }
    case ActionType.RetrieveTransactionAuthRequest:
      {
        const transactionToken = state.formattedDetail.transactionToken;
        if (transactionToken === null) {
          return [{
            ...state,
            hasBladeError: true
          }, () => of(setDetailsLoadingAction(false))];
        }
        const work = () => {
          const action = context.retrieveTransactionAuth(ExternalRetrieveAuthStateRequest.create({
            transactionToken
          })).then(retrieveTransactionAuthResponseAction);
          return from(action);
        };
        return [state, work];
      }
    case ActionType.RetrieveTransactionAuthResponse:
      {
        const {
          response
        } = action;
        if (response instanceof Error) {
          context.sentry.captureException(response);
          return [{
            ...state,
            hasBladeError: true
          }, () => of(setDetailsLoadingAction(false))];
        }
        return [{
          ...state,
          hasBladeError: false,
          formattedDetail: {
            ...state.formattedDetail,
            transactionAuthState: response.authState
          }
        }, () => of(setDetailsLoadingAction(false))];
      }
    case ActionType.RecognizeTransactionRequest:
      {
        const {
          isRecognized
        } = action;
        const transactionToken = state.formattedDetail.transactionToken;
        if (transactionToken === null) {
          return [{
            ...state,
            hasBladeError: true
          }, () => of(setDetailsLoadingAction(false))];
        }
        const work = () => {
          const action = context.recognizeTransaction(ExternalOverrideDeclinedAuthRequest.create({
            transactionToken,
            isRecognized
          })).then(recognizeTransactionResponseAction);
          return concat(of(setDetailsLoadingAction(true)), from(action));
        };
        return [state, work];
      }
    case ActionType.RecognizeTransactionResponse:
      {
        const {
          response
        } = action;
        if (response instanceof Error) {
          context.sentry.captureException(response);
          return [{
            ...state,
            hasBladeError: true
          }, () => of(setDetailsLoadingAction(false))];
        }
        return [{
          ...state,
          hasBladeError: false,
          formattedDetail: {
            ...state.formattedDetail,
            transactionAuthState: response.authState
          }
        }, () => of(setDetailsLoadingAction(false))];
      }
    case ActionType.BootstrapPrintableCheckManagementRequest:
      {
        let checkToken;
        let transferToken;
        state.formattedDetail.attachments?.forEach(attachment => {
          checkToken = attachment.checkImage?.checkToken;
          transferToken = attachment.checkImage?.transferToken;
        });
        if (!checkToken || !transferToken) {
          return [state, () => of(setDetailsLoadingAction(false))];
        }
        const work = () => {
          const action = context.getBootstrapPrintableCheckManagement(BootstrapPrintableCheckManagementRequestProto.create({
            printableCheckId: checkToken,
            transferToken
          })).then(bootstrapPrintableCheckManagementResponseAction);
          return from(action);
        };
        return [{
          ...state,
          printableCheckToken: checkToken,
          printableCheckTransferToken: transferToken
        }, work];
      }
    case ActionType.BootstrapPrintableCheckManagementResponse:
      {
        const {
          response
        } = action;
        if (response instanceof Error) {
          context.sentry.captureException(response);
          return [{
            ...state,
            hasBladeError: true
          }, () => of(setDetailsLoadingAction(false))];
        }
        const checkManagementLogEvent = response.logEvents?.viewLogEvent;
        if (checkManagementLogEvent) {
          context.logEvents.track(checkManagementLogEvent);
        }
        return [{
          ...state,
          hasBladeError: false,
          printableCheckManagement: response
        }, () => of(setDetailsLoadingAction(false))];
      }
    case ActionType.CancelPrintableCheckRequest:
      {
        const {
          printableCheckToken,
          printableCheckTransferToken,
          printableCheckManagement
        } = state;
        if (!printableCheckToken || !printableCheckTransferToken) {
          context.sentry.captureMessage('Printable check blade is missing printableCheckToken or printableCheckTransferToken');
          return [{
            ...state,
            hasBladeError: true
          }, null];
        }
        const checkManagementLogEvent = printableCheckManagement?.logEvents?.cancellationConfirmationLogEvents?.clickConfirmLogEvent;
        if (checkManagementLogEvent) {
          context.logEvents.track(checkManagementLogEvent);
        }
        const work = () => {
          const action = context.cancelPrintableCheck(CancelPrintableCheckRequestProto.create({
            printableCheckId: printableCheckToken,
            transferToken: printableCheckTransferToken
          })).then(cancelPrintableCheckResponseAction);
          return from(action);
        };
        return [{
          ...state,
          isCancelPrintableCheckLoading: true
        }, work];
      }
    case ActionType.CancelPrintableCheckResponse:
      {
        const {
          response
        } = action;
        if (response.error) {
          context.sentry.captureException(response);
          return [{
            ...state,
            cancelPrintableCheckError: response.error,
            isCancelPrintableCheckLoading: false
          }, null];
        }
        const {
          bladeActivityId
        } = state;
        const work = bladeActivityId !== undefined ? () => activityDetailsRequestAction(bladeActivityId) : null;
        return [{
          ...state,
          isCancelPrintableCheckLoading: false,
          showCancelPrintableCheckDialog: false
        }, work];
      }
    case ActionType.ToggleCancelPrintableCheck:
      {
        const toggle = !state.showCancelPrintableCheckDialog;
        const checkManagementLogEvent = toggle ? state.printableCheckManagement?.logEvents?.clickCancelLogEvent : state.printableCheckManagement?.logEvents?.cancellationConfirmationLogEvents?.clickDoNothingLogEvent;
        if (checkManagementLogEvent) {
          context.logEvents.track(checkManagementLogEvent);
        }
        return [{
          ...state,
          showCancelPrintableCheckDialog: toggle
        }, null];
      }
    case ActionType.PendingActivitiesRequest:
      {
        const work = () => {
          const batchRequest = state.pendingPaginationToken === null ? undefined : {
            batchToken: state.pendingPaginationToken,
            batchSize: state.config.activityPageSize
          };
          const responseAction = context.getActivities(state.query, 'pending', batchRequest).then(response => pendingActivitiesResponseAction(state.query, response));
          const setLoading = state.pendingPaginationToken ? setPaginationLoadingAction : setLoadingAction;

          // If loading has already been set, we are loading both pending and completed activities
          // Do not set loading to false here, and only set it to false in completed response
          // We have to use this hack because workflow does not support batching actions together
          // such that we could setLoading(true) for 2 actions and setLoading(false) after
          if (state.loading) {
            return responseAction;
          } else {
            return concat(of(setLoading(true)), from(responseAction), of(setLoading(false)));
          }
        };
        return [state, work];
      }
    case ActionType.PendingActivitiesResponse:
      {
        const {
          query,
          data
        } = action;

        // If request is stale, ignore the response
        if (isStaleRequest(query, state.query)) {
          return [state, null];
        }
        if (data instanceof Error) {
          context.sentry.captureException(data);
          return [{
            ...state,
            hasError: true
          }, null];
        }
        const {
          activities: pendingBalanceReportItems,
          paginationToken: pendingPaginationToken
        } = data;
        const pendingActivities = [...state.pendingActivities, ...pendingBalanceReportItems];
        const activityList = {
          ...state.activityList,
          pending: pendingActivities
        };
        let exportLink = '';
        if (state.config.requireExportLink) {
          try {
            exportLink = getExportLink(state);
          } catch {
            return [{
              ...state,
              activityList,
              hasError: true,
              pendingActivities,
              pendingPaginationToken
            }, null];
          }
        }
        return [{
          ...state,
          activityList,
          exportLink,
          pendingActivities,
          pendingPaginationToken
        }, null];
      }
    case ActionType.CloseBundle:
      {
        const {
          bundleId
        } = action;
        state.expandedBundles.delete(bundleId);

        // Set is mutable, so return same state.
        return [{
          ...state
        }, null];
      }
    case ActionType.CompletedActivitiesRequest:
      {
        const work = () => {
          const batchRequest = state.completedPaginationToken === null ? undefined : {
            batchToken: state.completedPaginationToken,
            batchSize: state.config.activityPageSize
          };
          const action = context.getActivities(state.query, 'completed', batchRequest).then(response => completedActivitiesResponseAction(state.query, response));
          const setLoading = state.completedPaginationToken ? setPaginationLoadingAction : setLoadingAction;

          // If loading has already been set, we are loading both pending and completed activities
          // We setLoading to false here because both actions have completed
          // We have to use this hack because workflow does not support batching actions together
          // such that we could setLoading(true) for 2 actions and setLoading(false) after
          if (state.loading) {
            return concat(from(action), of(setLoading(false)));
          } else {
            return concat(of(setLoading(true)), from(action), of(setLoading(false)));
          }
        };
        return [state, work];
      }
    case ActionType.CompletedActivitiesResponse:
      {
        const {
          query,
          data
        } = action;

        // If request is stale, ignore the response
        if (isStaleRequest(query, state.query)) {
          return [state, null];
        }
        if (data instanceof Error) {
          context.sentry.captureException(data);
          return [{
            ...state,
            hasError: true
          }, null];
        }
        const {
          activities: completedBalanceReportItems,
          paginationToken: completedPaginationToken
        } = data;
        const completedActivities = [...state.completedActivities, ...completedBalanceReportItems];
        const groupedActivities = groupCompletedActivitiesByDay(completedActivities);
        const activityList = {
          ...state.activityList,
          completed: groupedActivities
        };
        let exportLink = '';
        if (state.config.requireExportLink) {
          try {
            exportLink = getExportLink(state);
          } catch {
            return [{
              ...state,
              activityList,
              completedActivities,
              completedPaginationToken,
              exportLink
            }, null];
          }
        }
        return [{
          ...state,
          activityList,
          completedActivities,
          completedPaginationToken,
          exportLink
        }, null];
      }
    case ActionType.OpenBundle:
      {
        const {
          bundleId
        } = action;
        state.expandedBundles.add(bundleId);

        // Set is mutable, so return same state.
        return [{
          ...state
        }, null];
      }
    case ActionType.SetDetailsLoading:
      {
        const {
          loadingDetails
        } = action;
        return [{
          ...state,
          loadingDetails
        }, null];
      }
    case ActionType.SetLoading:
      {
        const {
          loading
        } = action;
        return [{
          ...state,
          loading
        }, null];
      }
    case ActionType.SetPaginationLoading:
      {
        const {
          loadingNextPage
        } = action;
        return [{
          ...state,
          loadingNextPage
        }, null];
      }
    case ActionType.Reset:
      {
        // Reset state in preparation for getting a new set of activity.
        return [{
          ...INITIAL_STATE,
          config: {
            ...state.config
          },
          query: {
            ...state.query
          }
        }, null];
      }
    case ActionType.UpdateDateMenuSelection:
      {
        const {
          selectedDateCategory
        } = action;

        // NOTE(trevorfrese): Why is this not included in UpdateQuery?
        // This is a special action - it updates UI state for the selected date
        // which comes from the Market Date Picker, and by itself, this change
        // should not trigger a reload of activities, because it only changes a string label.
        return [{
          ...state,
          query: {
            ...state.query,
            selectedDateCategory
          }
        }, null];
      }
    case ActionType.UpdateDateRange:
      {
        const {
          startAt,
          endAt
        } = action;
        const {
          isBladeOpen
        } = state;
        const work = isBladeOpen ? () => of(goToActivityRouteAction(), updateQueryAction({
          startAt,
          endAt
        })) : () => updateQueryAction({
          startAt,
          endAt
        });
        return [state, work];
      }
    case ActionType.UpdateOwnersAndSetDateRange:
      {
        const {
          unitToken,
          startAt,
          endAt
        } = action;
        const owner = Owner.create({
          id: unitToken,
          type: OwnerType.UNIT
        });
        return [state, () => updateQueryAction({
          activityOwners: [owner],
          startAt,
          endAt
        })];
      }
    case ActionType.UpdateOwners:
      {
        const {
          unitToken
        } = action;

        // NOTE(@trevorfrese): The GetBalanceReport request takes a list of
        // owners. For now, that list is 1 unit token, because of the current
        // design of the backend endpoint. It may change in the future to accept
        // multiple tokens or to accept a merchant token

        const owner = Owner.create({
          id: unitToken,
          type: OwnerType.UNIT
        });
        return [state, () => updateQueryAction({
          activityOwners: [owner]
        })];
      }
    case ActionType.UpdateQuery:
      {
        const {
          query
        } = action;
        const work = () => of(resetAction(), setLoadingAction(true), pendingActivitiesRequestAction(), completedActivitiesRequestAction());
        return [{
          ...state,
          query: {
            ...state.query,
            ...query
          }
        }, work];
      }
    case ActionType.UpdateSelectedType:
      {
        const {
          selectedTypeCategory
        } = action;
        const {
          isBladeOpen
        } = state;
        const work = isBladeOpen ? () => of(goToActivityRouteAction(), updateQueryAction({
          selectedTypeCategory
        })) : () => updateQueryAction({
          selectedTypeCategory
        });
        return [state, work];
      }
    case ActionType.Scroll:
      {
        if (state.pendingPaginationToken) {
          return [state, pendingActivitiesRequestAction];
        } else if (state.completedPaginationToken) {
          return [state, completedActivitiesRequestAction];
        } else {
          return [state, null];
        }
      }
    case ActionType.ViewInTransaction:
    case ActionType.GoToActivityRoute:
      {
        // ViewInTransaction & GoToActivityRoute both trigger a route transition
        // the transition logic is shifted to the related controller(blade) for now to
        // avoid tight coupling between ember router and workflow
        return [state, null];
      }
    case ActionType.UpdateCategory:
      {
        const {
          updatedCategoryId,
          currentCategoryId
        } = action.update;
        const {
          id,
          findataTransactionInfo
        } = state.formattedDetail;
        const {
          name,
          properties
        } = clickUpdateCategory({
          updatedCategoryId,
          currentCategoryId,
          transactionId: findataTransactionInfo?.findataTransactionId
        });
        context.dataApi.track(name, properties);
        const work = () => {
          assert(id != null, 'Invalid id when updating category');
          assert(findataTransactionInfo?.findataAccountIdentifier != null, 'Invalid findataTransactionInfo when updating category');
          const transactionCategory = TransactionCategory.create({
            findataAccountIdentifier: findataTransactionInfo.findataAccountIdentifier,
            categoryId: updatedCategoryId,
            transactionToken: findataTransactionInfo.findataTransactionId
          });
          const request = UpdateCategoryRequest.create({
            idempotenceToken: uuid(),
            transactionCategory
          });
          const action = context.updateCategory(request).then(response => {
            return updateCategoryResponseAction(id, response);
          }).catch(showCategoryErrorAction);
          return concat(of(setDetailsLoadingAction(true)), from(action));
        };
        return [state, work];
      }
    case ActionType.UpdateCategoryResponse:
      {
        const {
          id,
          response
        } = action;
        return response.success ? [{
          ...state,
          showCategories: false
        }, () => concat(of(setDetailsLoadingAction(false)), of(activityDetailsRequestAction(id)), of(showToastAction(MarketToastTypes.SUCCESS, response.success?.successText)))] : [state, () => concat(of(setDetailsLoadingAction(false)), of(showToastAction(MarketToastTypes.CRITICAL, response.error?.errorText)))];
      }
    case ActionType.ShowCategoryError:
      {
        const work = () => {
          return concat(of(showToastAction(MarketToastTypes.CRITICAL, context.translate('banking.checking.unifiedActivity.activityDetails.nullState.error.message'))), of(setDetailsLoadingAction(false)));
        };
        return [state, work];
      }
    case ActionType.ListCategoriesError:
      {
        return [{
          ...state,
          hasBladeError: true
        }, showCategoryErrorAction];
      }
    case ActionType.ListCategories:
      {
        const work = () => {
          const action = context.listCategories(ListCategoriesRequest.create()).then(listCategoriesResponseAction).catch(listCategoriesErrorAction);
          return concat(of(setDetailsLoadingAction(true)), from(action));
        };
        return [{
          ...state,
          listCategoriesResponse: undefined
        }, work];
      }
    case ActionType.ListCategoriesResponse:
      {
        const {
          response
        } = action;
        return [{
          ...state,
          listCategoriesResponse: response
        }, () => of(setDetailsLoadingAction(false))];
      }
    case ActionType.ToggleCategories:
      {
        const showCategories = !state.showCategories;
        if (showCategories) {
          const {
            name,
            properties
          } = clickCategoryRow();
          context.dataApi.track(name, properties);
        }
        const work = showCategories ? () => listCategoriesAction() : null;
        return [{
          ...state,
          showCategories
        }, work];
      }
    case ActionType.OpenBlade:
      {
        const {
          isBladeOpen,
          id
        } = action;
        if (state.printableCheckManagement) {
          context.dismissPrintableCheckBlade();
        }
        return [{
          ...state,
          isBladeOpen,
          bladeActivityId: id,
          printableCheckManagement: undefined,
          printableCheckToken: undefined,
          printableCheckTransferToken: undefined,
          showCategories: false
        }, () => activityDetailsRequestAction(id)];
      }
    case ActionType.RemoveBlade:
      {
        const {
          isBladeOpen
        } = action;
        if (state.printableCheckManagement) {
          context.dismissPrintableCheckBlade();
        }
        return [{
          ...state,
          bladeActivityId: undefined,
          isBladeOpen,
          printableCheckManagement: undefined,
          printableCheckToken: undefined,
          printableCheckTransferToken: undefined
        }, null];
      }
    case ActionType.DownloadCheck:
      {
        context.downloadCheck(action.printableCheckId);
        const checkManagementLogEvent = state.printableCheckManagement?.logEvents?.clickDownloadLogEvent;
        if (checkManagementLogEvent) {
          context.logEvents.track(checkManagementLogEvent);
        }
        return [state, null];
      }
    case ActionType.PrintCheck:
      {
        context.printCheck(action.printableCheckId);
        const checkManagementLogEvent = state.printableCheckManagement?.logEvents?.clickPrintLogEvent;
        if (checkManagementLogEvent) {
          context.logEvents.track(checkManagementLogEvent);
        }
        return [state, null];
      }
    case ActionType.UpdateConfig:
      {
        const {
          foldersActivity
        } = action;
        return foldersActivity ? [{
          ...state,
          config: {
            requireExportLink: false,
            enablePostedBalance: false,
            typeCategories: [],
            activityPageSize: DEFAULT_BATCH_SIZE
          }
        }, null] : [{
          ...state,
          config: {
            requireExportLink: true,
            enablePostedBalance: true,
            typeCategories: TYPE_CATEGORIES,
            activityPageSize: DEFAULT_BATCH_SIZE
          }
        }, null];
      }
    case ActionType.ShowToast:
      {
        const {
          toastType,
          toastText
        } = action;
        if (toastText) {
          switch (toastType) {
            case MarketToastTypes.CRITICAL:
              context.marketToasts.critical(toastText);
              break;
            case MarketToastTypes.SUCCESS:
              context.marketToasts.success(toastText);
              break;
            case MarketToastTypes.WARNING:
              context.marketToasts.warning(toastText);
              break;
            case MarketToastTypes.INFO:
            default:
              context.marketToasts.info(toastText);
              break;
          }
        }
        return [state, null];
      }
  }
};
const unifiedActivity = (context, seed = {}) => workflow(update(context), {
  ...INITIAL_STATE,
  ...seed
});
export default unifiedActivity;